import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Trophy, Filter, AlertCircle, Eye } from "lucide-react"
import { TournamentStatusBadge } from "@/components/ui/TournamentStatusBadge"
import { useNavigate } from "react-router-dom"

export default function RecentTournaments({ recentTournaments, statusFilter, setStatusFilter }) {
  const navigate = useNavigate()

  return (
    <Card className="bg-card dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl transition-colors duration-300">
      {/* Header */}
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary dark:text-primary/80" />
          <CardTitle className="text-lg text-gray-900 dark:text-gray-100">
            Torneos Recientes
          </CardTitle>
          <Badge variant="secondary">{recentTournaments.length}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-700 dark:text-gray-300" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-border dark:border-gray-600">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ongoing">En Curso</SelectItem>
              <SelectItem value="finished">Finalizados</SelectItem>
              <SelectItem value="upcoming">Próximos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {recentTournaments.length > 0 ? (
          <>
            {/* Vista Desktop (Tabla) */}
            <div className="hidden md:block">
              <Table className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-gray-900 dark:text-gray-100">Torneo</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Tienda</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Fecha</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Estado</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentTournaments.map((tournament) => (
                    <TableRow
                      key={tournament.id}
                      className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <TableCell className="text-gray-900 dark:text-gray-100">
                        <div className="space-y-1">
                          <div className="font-bold">{tournament.name}</div>
                          <div className="text-sm text-muted-foreground dark:text-gray-300">
                            {tournament.Game?.name} • {tournament.TournamentType?.name} •{" "}
                            {tournament.Format?.name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-900 dark:text-gray-100">
                        <div className="font-medium">{tournament.Store?.name}</div>
                      </TableCell>
                      <TableCell className="text-gray-900 dark:text-gray-100">
                        {new Date(tournament.startDate).toLocaleDateString("es-ES", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </TableCell>
                      <TableCell>
                        <TournamentStatusBadge statusId={tournament.statusId} />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-gray-900 dark:text-gray-100 border border-border dark:border-gray-600"
                          onClick={() => navigate(tournament.urlView)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Vista Mobile (Cards) */}
            <div className="grid gap-4 md:hidden">
              {recentTournaments.map((tournament) => (
                <div
                  key={tournament.id}
                  className="rounded-xl border border-border dark:border-gray-700 p-4 shadow-sm bg-white dark:bg-gray-800 transition-colors"
                >
                  <div>
                    <h3 className="font-bold text-base text-gray-900 dark:text-gray-100">
                      {tournament.name}
                    </h3>
                    <p className="text-sm text-muted-foreground dark:text-gray-300">
                      {tournament.Game?.name} • {tournament.TournamentType?.name} •{" "}
                      {tournament.Format?.name}
                    </p>
                  </div>

                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    <span className="font-medium">Tienda: </span>
                    {tournament.Store?.name}
                  </div>

                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    <span className="font-medium">Fecha: </span>
                    {new Date(tournament.startDate).toLocaleDateString("es-ES", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>

                  <div>
                    <TournamentStatusBadge statusId={tournament.statusId} />
                  </div>

                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-gray-900 dark:text-gray-100 border border-border dark:border-gray-600"
                      onClick={() => navigate(tournament.urlView)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver Torneo
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Alert className="mt-4 bg-yellow-50 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700 text-yellow-900 dark:text-yellow-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Solo se lista tu participación de los últimos 10 torneos.
              </AlertDescription>
            </Alert>
          </>
        ) : (
          <div className="text-center py-8">
            <Trophy className="h-12 w-12 text-muted-foreground dark:text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground dark:text-gray-300">
              No se encontraron torneos con los filtros seleccionados
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
